import { FC } from 'react';
import { Avatar, Tag, Typography } from 'antd';
import { BsLinkedin } from 'react-icons/bs';
import { FaFacebook } from 'react-icons/fa';
import { FaSquareXTwitter } from 'react-icons/fa6';
import { SiCrunchbase } from 'react-icons/si';
import { TbCubePlus, TbLink, TbSquareRoundedCheckFilled } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DashedPane, EmptySection, VirtualizedList as List } from '@/shared';
import { corporateLogo } from '@/utilities';

interface BulkSelectProps {
    resultsSelected: any;
    setResultsSelected: any;
    filteredResults: any;
    setFilteredResults: any;
}

const BulkSelect: FC<BulkSelectProps> = ({ filteredResults, resultsSelected }): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            <Typography.Title level={5}>1. Bulk Select</Typography.Title>
            <div style={{ margin: '0.5rem 0', marginTop: 0 }}>
                <small style={{ color: token.colorTextTertiary }}>
                    <TbSquareRoundedCheckFilled style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                    Selected Companies
                    {resultsSelected?.size !== 0 && (
                        <span style={{ marginLeft: '0.25rem' }}>({resultsSelected?.size})</span>
                    )}
                </small>
            </div>
            <DashedPane height='auto' style={{ flex: 1, minHeight: 0 }}>
                {filteredResults?.length !== 0 ? (
                    <List
                        size='small'
                        style={{ height: '100%', overflowY: 'auto' }}
                        dataSource={filteredResults}
                        renderItem={(result: any) => {
                            return (
                                <List.Item key={result?.symbol} style={{ padding: '0.5rem 0' }}>
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar
                                                shape='square'
                                                size='large'
                                                style={{
                                                    verticalAlign: '-1.825rem',
                                                    border: `1px solid ${token.colorBorder}`,
                                                }}
                                                src={
                                                    result?.enrichment?.website &&
                                                    corporateLogo(result?.enrichment?.website)
                                                }
                                            />
                                        }
                                        title={
                                            <span style={{ fontWeight: 500 }}>
                                                <span>{result?.name}</span>
                                                <Tag
                                                    style={{
                                                        borderColor: 'transparent',
                                                        marginLeft: '0.5rem',
                                                    }}
                                                >
                                                    {result?.enrichment?.exchange && (
                                                        <strong>{result?.enrichment?.exchange}: </strong>
                                                    )}
                                                    <span style={{ fontWeight: 'normal' }}>{result?.symbol}</span>
                                                </Tag>
                                            </span>
                                        }
                                        description={
                                            <span
                                                style={{ verticalAlign: '-0.125rem', color: token.colorTextTertiary }}
                                            >
                                                {result?.enrichment?.website && (
                                                    <a
                                                        href={result?.enrichment?.website}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        style={{ color: 'inherit' }}
                                                    >
                                                        <TbLink size='0.875rem' />
                                                    </a>
                                                )}
                                                {result?.enrichment?.clearbit?.linkedin?.handle && (
                                                    <a
                                                        href={`https://linkedin.com/${result?.enrichment?.clearbit?.linkedin?.handle}`}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        style={{ color: 'inherit' }}
                                                    >
                                                        <BsLinkedin
                                                            size='0.875rem'
                                                            style={{
                                                                marginLeft: '0.5rem',
                                                                borderRadius: token.borderRadius,
                                                            }}
                                                        />
                                                    </a>
                                                )}
                                                {result?.enrichment?.clearbit?.twitter?.handle && (
                                                    <a
                                                        href={`http://x.com/${result?.enrichment?.clearbit?.twitter?.handle}`}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        style={{ color: 'inherit' }}
                                                    >
                                                        <FaSquareXTwitter
                                                            size='1rem'
                                                            style={{
                                                                marginLeft: '0.5rem',
                                                                verticalAlign: '-0.05rem',
                                                            }}
                                                        />
                                                    </a>
                                                )}
                                                {result?.enrichment?.clearbit?.crunchbase?.handle && (
                                                    <a
                                                        href={`https://crunchbase.com/${result?.enrichment?.clearbit?.crunchbase?.handle}`}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        style={{ color: 'inherit' }}
                                                    >
                                                        <SiCrunchbase
                                                            size='0.875rem'
                                                            style={{
                                                                marginLeft: '0.5rem',
                                                                borderRadius: token.borderRadius,
                                                            }}
                                                        />
                                                    </a>
                                                )}
                                                {result?.enrichment?.clearbit?.facebook?.handle && (
                                                    <a
                                                        href={`https://facebook.com/${result?.enrichment?.clearbit?.facebook?.handle}`}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        style={{ marginLeft: '0.5rem', color: 'inherit' }}
                                                    >
                                                        <FaFacebook size='0.875rem' />
                                                    </a>
                                                )}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                ) : (
                    <EmptySection
                        height='100%'
                        icon={<TbCubePlus size='1rem' style={{ color: token.colorTextTertiary }} />}
                        label='No Companies Selected'
                        description='Select at least one search result before syncing company data to your data sources.'
                    />
                )}
            </DashedPane>
        </div>
    );
};

export default BulkSelect;
